import { Injectable } from '@angular/core';
import { Culture, GetLocalizationListRequest } from '@ats/api/cm';
import { SettingsService } from '@cm/core/settings';
import { CmEventService } from '@cm/core/system';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, Observable, Subject, combineLatest, of, throwError } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	first,
	map,
	switchMap,
	take,
} from 'rxjs/operators';
import { ERROR_GUIDS } from '../data/error-mappings';
import { CmCulture, PublicLocalization } from '../models';
import { CmAuthService } from './auth.service';

const LOCALIZATION_QUERY = gql`
	query publicLocalizations($request: GetLocalizationListRequest!) {
		clientSponsor {
			localizationList(request: $request) {
				name
				type
				value
				defaultValue
				htmlAllowed
			}
		}
	}
`;

const enum LocalizationActionTypes {
	UPDATE_LOCALIZATIONS = 'Update localizations',
	UPDATE_IS_LOCALIZING = 'Update the localizing status',
	UPDATE_LOCALIZE_DATA = 'Update active localization data',
}
interface LocalizationAction {
	readonly type: LocalizationActionTypes;
	readonly payload:
		| CmCulture
		| CmCulture[]
		| Culture[]
		| boolean
		| PublicLocalization
		| PublicLocalization[];
}

class LocalizationState {
	localizations: PublicLocalization[] = [];
	isLocalizing: boolean = false;
	activeLocalization: PublicLocalization = null;
}

const reduce = (state: LocalizationState, action: LocalizationAction) => {
	switch (action.type) {
		case LocalizationActionTypes.UPDATE_LOCALIZATIONS:
			return {
				...state,
				localizations: [...(action.payload as PublicLocalization[]).map(loc => loc)],
			};
		case LocalizationActionTypes.UPDATE_IS_LOCALIZING:
			return {
				...state,
				isLocalizing: action.payload as boolean,
			};
		case LocalizationActionTypes.UPDATE_LOCALIZE_DATA:
			return {
				...state,
				activeLocalization: action.payload
					? { ...(action.payload as PublicLocalization) }
					: null,
			};
	}
};

/**
 * @deprecated Use @cm/core/localization LocalizationService instead
 */
@UntilDestroy()
@Injectable({
	providedIn: 'root',
})
export class CmLocalizationService {
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	missingLocalizationSub$: Subject<string> = new Subject<string>();
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	missingLocalization$: Observable<string> = this.missingLocalizationSub$.asObservable();
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	localizationSnapshot: PublicLocalization[] = [];

	private state = new LocalizationState();
	private dispatch = new BehaviorSubject<LocalizationState>(this.state);
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	localization$: Observable<PublicLocalization[]> = this.dispatch
		.asObservable()
		.pipe(map(state => state.localizations));
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	isLocalizing$: Observable<boolean> = this.dispatch.asObservable().pipe(
		map(state => state.isLocalizing),
		distinctUntilChanged()
	);
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	activeLocalization$: Observable<PublicLocalization> = this.dispatch.asObservable().pipe(
		map(state => state.activeLocalization),
		distinctUntilChanged(this.distinctUntilChangePredicate)
	);

	/**
	 * @deprecated
	 * Use supportedCultures$ on the CmSettingsService
	 */
	readonly possibleSupported$: Observable<CmCulture[]> = this.settings.supportedCultures$;

	/**
	 * @deprecated
	 * Use clientCultures$ on the CmSettingsService
	 */
	readonly supported$: Observable<CmCulture[]> = this.settings.existingCultures$;

	/**
	 * @deprecated
	 * Use  @cm/core/settings SettingsService.culture$
	 */
	culture$: Observable<CmCulture> = this.settings.culture$;

	constructor(
		private readonly apollo: Apollo,
		private readonly eventService: CmEventService,
		private readonly auth: CmAuthService,
		private readonly settings: SettingsService
	) {
		this.auth.token$
			.pipe(filter(token => !token))
			.subscribe(() => this.updateIsLocalizing(false));
	}
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	init(): Observable<boolean> {
		// This has to be here. In the constructor creates a circular dependency since the logging
		// HTTP interceptors relies on the localization service
		this.instantiateLocalizations();

		const localizations$ = this.localization$.pipe(
			filter(localization => (localization || []).length > 0),
			take(1)
		);

		return localizations$.pipe(
			catchError(err => throwError(err)),
			switchMap(() => of(true))
		);
	}
	/**
	 *
	 * @deprecated - Use localizationService.find$ and do the rest yourself
	 */
	localizationHasValue$(locKey: string, ignoreDefault: boolean = false): Observable<boolean> {
		return this.localization$.pipe(
			map(locs => !!locs.find(loc => loc.name === locKey)?.value?.length)
		);
	}
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	toggleLocalization(): void {
		this.setLocalizationState(!(this.state || ({} as LocalizationState)).isLocalizing);
	}
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	setLocalizationState(isLocalizing: boolean): void {
		if (!isLocalizing) {
			this.updateIsLocalizing(isLocalizing);
		} else {
			// Only turn it on if they are authorized
			this.auth.token$
				.pipe(
					first(),
					map(token => (token || {}).localizationEnabled)
				)
				.subscribe(isAuthorized => {
					if (isAuthorized) {
						this.updateIsLocalizing(isLocalizing);
					}
				});
		}
	}
	/**
	 * @deprecated - Just don't use it, okay?
	 */
	triggerLocalization(localization: PublicLocalization = null): void {
		const type = LocalizationActionTypes.UPDATE_LOCALIZE_DATA;
		const action = { payload: localization, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}
	/**
	 * @deprecated - Use LocalizationService.findGUIDLocName and find$
	 */
	mapErrorGUID(guid?: string, isKey: boolean = false): string {
		let locKey: string;
		if (isKey) {
			locKey = guid;
		} else {
			// Default to server error
			const guidMap: string =
				!guid || guid === '' ? 'Core/Error.GUID.ServerError' : this.getErrorMap(guid);

			locKey = guidMap ? guidMap : 'Core/Error.GUID.ServerError';
		}
		const errorLoc = this.state.localizations.find(loc => loc.name === locKey);
		return errorLoc
			? errorLoc.value
			: 'An error has occurred and has been reported. Please try your request again later.';
	}
	/**
	 * @deprecated - Use LocalizationService.findGUIDLocName
	 */
	getErrorMap(guid?: string): string {
		return ERROR_GUIDS[`GUID_${guid}`];
	}
	/**
	 * @deprecated - Use localizationService.find$ - Make it an observable
	 */
	localize(key: string, defaultValue: string): string {
		const localization = this.findLocalization(key);
		return localization?.value || defaultValue;
	}
	/**
	 * @deprecated - Use localizationService.find$
	 */
	localize$(key: string, defaultValue: string): Observable<string> {
		return this.findLocalization$(key).pipe(
			map(localization => localization?.value || defaultValue)
		);
	}
	/**
	 * @deprecated - Use localizationService.find$ - Make it an observable
	 */
	findLocalization(key: string, strict: boolean = true): PublicLocalization {
		return this.getLocalizationFromList(key, strict);
	}
	/**
	 * @deprecated - Use localizationService.find$
	 */
	findLocalization$(key: string, strict: boolean = true): Observable<PublicLocalization> {
		return this.localization$.pipe(
			map(locs => this.getLocalizationFromList(key, strict, locs))
		);
	}

	/**
	 * @deprecated - Use LocalizationViewService.popSystemError
	 * Displays the generic system error snackbar
	 * @method
	 * @param {string} [id='system-error'] - The id to use for displaying the error
	 * @return {void}
	 */
	popLocalizedSystemError(id: string = 'system-error') {
		this.eventService.handleSystemError(id);
	}

	private getLocalizationFromList(
		key: string,
		strict: boolean,
		list: PublicLocalization[] = [...this.state.localizations]
	): PublicLocalization {
		const localization =
			list.find(loc => (strict ? loc.name === key : loc.name.includes(key))) || null;
		if (!localization) {
			this.reportMissingLocalization(key);
		}
		return localization;
	}

	private reportMissingLocalization(key: string) {
		this.missingLocalizationSub$.next(key);
	}

	private updateIsLocalizing(payload: boolean) {
		const type = LocalizationActionTypes.UPDATE_IS_LOCALIZING;
		const action = { payload, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private updateLocalizations(localizations: PublicLocalization[]) {
		this.localizationSnapshot = (localizations || []).slice();
		const type = LocalizationActionTypes.UPDATE_LOCALIZATIONS;
		const action = { payload: localizations, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private instantiateLocalizations(): void {
		combineLatest([this.settings.cultureCode$, this.settings.client$])
			.pipe(
				switchMap(
					([culture, client]) =>
						this.apollo.watchQuery<{
							clientSponsor: { name: string; localizationList: PublicLocalization[] };
							request: GetLocalizationListRequest;
						}>({
							query: LOCALIZATION_QUERY,
							variables: {
								request: {
									clientAlias: client,
									cultureName: culture,
									portalType: this.settings.portal,
								},
							},
						}).valueChanges
				),
				map(({ data }) => (data.clientSponsor || {}).localizationList || []),
				untilDestroyed(this)
			)
			.subscribe(localizations => this.updateLocalizations(localizations));
	}

	private distinctUntilChangePredicate(a: any, b: any): boolean {
		if (a === b) {
			return true;
		}
		if (a && b && a.code === b.code) {
			return true;
		}
		return false;
	}
}
